import { DateService, IndexedDBService, Quote } from '@pulse/browser-shared';
import { Experiment, SettingList, SettingsConfigurations, objectDeepClone } from '@pulse/common';
import { CachedSetting, SettingIndexedDB } from '../models';
import { ResponseTrackerService } from './response-tracker.service';

export class CacheService {
    private settingStorageKey = 'settingsStore';
    private storedSettings: CachedSetting = {};

    constructor(
        private indexedDBService: IndexedDBService,
        private dateService: DateService,
        private responseTrackerService: ResponseTrackerService,
        private quote: Quote,
        private config: SettingsConfigurations,
    ) {
        this.responseTrackerService.responses().subscribe((response) => {
            if (response.settings && response.tag) {
                this.cacheSettings(
                    response.settings,
                    response.tag,
                    response.emptyForTags,
                    response.attributesHash,
                );
            }
        });
    }

    clearCache() {
        this.storedSettings = {};
        this.indexedDBService.truncate(this.settingStorageKey);
    }

    updateInMemorySettings(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.indexedDBService
                .getAllValue<SettingIndexedDB[]>(this.settingStorageKey)
                .then((settings) => {
                    if (settings) {
                        settings.forEach((setting: SettingIndexedDB) => {
                            if (setting.time < this.dateService.now() - this.config.cacheTimeout) {
                                this.indexedDBService.deleteValue(
                                    this.settingStorageKey,
                                    setting.id,
                                );
                            } else {
                                this.storedSettings[setting.id] = setting;
                            }
                        });
                    }
                    resolve(true);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    getCachedSettings(settingName: string) {
        const storedSetting = this.storedSettings[settingName];

        if (storedSetting) {
            const timedOut = storedSetting.time + this.config.cacheTimeout < this.dateService.now();

            return { cachedSetting: objectDeepClone(storedSetting), timedOut };
        }

        return undefined;
    }

    cacheSettings(
        settings: SettingList,
        tag: string,
        emptyForTags: string[],
        attributesHash: string,
    ) {
        const time = this.dateService.now();

        this.quote
            .quoteSize()
            .then((freeSpace) => {
                const newSettingsSize = new TextEncoder().encode(JSON.stringify(settings)).length;
                const enoughSpace = freeSpace && freeSpace - newSettingsSize > 1000000;
                for (const [key, value] of Object.entries(settings)) {
                    const emptyForTagsString = emptyForTags.join();

                    if (enoughSpace || freeSpace === null) {
                        this.indexedDBService.putValue(this.settingStorageKey, {
                            id: key,
                            [key]: value,
                            time,
                            tag,
                            attributesHash,
                            emptyForTags: emptyForTagsString,
                        });
                    }
                }
            })
            .catch((err) => console.log(err));

        for (const [key, value] of Object.entries(settings)) {
            const emptyForTagsString = emptyForTags.join();
            // this.usageTrackerService.settingChanged(key, value);
            this.storedSettings[key] = {
                [key]: value,
                time,
                tag,
                attributesHash,
                emptyForTags: emptyForTagsString,
                version: this.config.app_version,
            };
        }
        this.clearTimedOutSettings(tag);
    }

    removeCachedSettings(experiments: Experiment[]) {
        experiments.forEach((experiment: Experiment) => {
            if (experiment?.settings?.length) {
                experiment.settings.forEach((setting) => {
                    this.indexedDBService.deleteValue(this.settingStorageKey, setting);
                    delete this.storedSettings[setting];
                });
            }
        });
    }

    clearTimedOutSettings(tag: string) {
        for (const [key, value] of Object.entries(this.storedSettings)) {
            if (
                value['tag'] === tag &&
                value.time < this.dateService.now() - this.config.cacheTimeout
            ) {
                delete this.storedSettings[key];
            }
        }

        this.indexedDBService
            .getAllValue<SettingIndexedDB[]>(this.settingStorageKey)
            .then((settingsIndexedDB) => {
                if (settingsIndexedDB) {
                    settingsIndexedDB.forEach((setting: SettingIndexedDB) => {
                        if (
                            setting.tag === tag &&
                            setting.time < this.dateService.now() - this.config.cacheTimeout
                        ) {
                            this.indexedDBService.deleteValue(this.settingStorageKey, setting.id);
                        }
                    });
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }

    getAllTags({ ur, all = false }: { ur: string; all?: boolean }): Set<string> {
        const storedSettings = this.storedSettings;
        const allTags: Set<string> = new Set();

        if (storedSettings) {
            for (const [key] of Object.entries(storedSettings)) {
                if (storedSettings[key]['ur'] !== ur || all) {
                    allTags.add(storedSettings[key].tag);
                }
            }
        }

        return allTags;
    }
}
